import React, { useState, useEffect } from "react";
import { Form, Formik, useFormikContext, FormikErrors } from "formik";
import * as Yup from "yup";
import debounce from "lodash.debounce";
import isEmpty from "lodash.isempty";
import { PROFILE_TYPE_CONSULTANT } from "utils/constants";
import { useCurrentCompany } from "hooks/auth";

// Domain-specific form inputs (no newui equivalent)
import { TabHeader } from "components/TabHeader";
import { SmartFieldNote } from "components/RFQ/SmartFieldEditor/_components/SmartFieldNote";
import { Location } from "components/FormInputs/Location";
import { FormattedInput } from "components/FormInputs/FormattedInput";
import { Trades } from "components/FormInputs/Trades";
import { Currency } from "components/FormInputs/Currency";
import { TradeValue } from "components/FormInputs/TradeValue";
import { YearCompleted } from "components/ProjectForm/components/YearCompleted";
import { ProcurementMethodSelector } from "components/FormInputs/ProcurementMethodSelector";
import { ExpertiseRepeater } from "components/FormInputs/ExpertiseRepeater";
import { FlashError } from "components/FormInputs/FlashError";
import { FormButtonRow } from "components/FormButtonRow";
import { OnSubmitValidationError } from "utils/OnSubmitValidationError";

// newui components
import { FormikInputWithLabel, SelectOption } from "newui/components/ui/InputWithLabel";
import { CheckboxWithLabel } from "newui/components/ui/CheckboxWithLabel";
import { FormikMultiImagePreview } from "newui/components/ui/ImagePreview";
import { FormLabel } from "newui/components/ui/FormLabel";
import { Button } from "newui/components/Button";
import { FileUpload } from "components/RFQ/FileUpload";
import { Skeleton } from "@chakra-ui/react";
import { SectorSelect } from "newui/components/ui/CommonSelects";
import { PastProject } from "newui/features/Company/types";

const fundingTypeOptions = [
	{ label: "N/A", value: "" },
	{ label: "Public Project", value: "Public Project" },
	{ label: "Private Project", value: "Private Project" },
	{ label: "Public and Private Project", value: "Public and Private Project" },
	{ label: "Confidential", value: "Confidential" },
];

interface AutoSaveProps {
	debounceMs: number;
	isSubmitting: boolean;
}

const AutoSave: React.FC<AutoSaveProps> = ({ debounceMs, isSubmitting }) => {
	const formik = useFormikContext<PastProject>();
	formik.values.status = "published";

	const debouncedSubmit = React.useCallback(
		debounce(() => formik.submitForm(), debounceMs),
		[formik.submitForm]
	);

	useEffect(() => {
		if (!isSubmitting) {
			debouncedSubmit();
		}
	}, [debouncedSubmit, isSubmitting]);

	return null;
};

interface ProjectFormProps {
	additionalFields?: React.ReactNode;
	autoUpdateForm?: boolean;
	customButtons?: React.ReactNode;
	editForm?: boolean;
	errorStatus: boolean;
	hideDefaultButtons?: boolean;
	initialValues: PastProject;
	isRequesting?: boolean;
	profileTypeId?: number;
	savedField?: PastProject;
	supplierSmartField?: boolean;
	handleSubmit: (values: PastProject, helpers: { setErrors: (errors: FormikErrors<PastProject>) => void }) => void;
	setErrorStatus: (value: boolean) => void;
	handleDelete?: () => void;
	setAutoUpdateForm?: (value: boolean) => void;
}

const errorMessages: Record<string, { description: string; heading: string; }> = {
	required: {
		heading: "* Required Field",
		description: "There are incomplete required fields. Please complete them.",
	},
	connection: {
		heading: "Connection not established",
		description: "Unable to process your request",
	},
	maxCharacter: {
		heading: "Input has reached max character",
		description: "Input has reached max character",
	},
};

export const ProjectForm: React.FC<ProjectFormProps> = ({
	profileTypeId,
	handleSubmit,
	initialValues,
	isRequesting,
	setAutoUpdateForm,
	autoUpdateForm,
	editForm,
	handleDelete,
	errorStatus,
	setErrorStatus,
	savedField,
	additionalFields,
	customButtons,
	hideDefaultButtons = false,
	supplierSmartField = false,
}) => {
	const [siteAreaChecked, setSiteAreaChecked] = useState(false);
	const [grossAreaChecked, setGrossAreaChecked] = useState(false);

	useEffect(() => {
		setSiteAreaChecked(!!initialValues.site_area);
		setGrossAreaChecked(!!initialValues.gross_floor_area);
	}, [initialValues]);

	const currentCompany = useCurrentCompany();
	const profileType = profileTypeId || currentCompany?.profile_type?.id;

	const validationSchema = Yup.object({
		name: Yup.string().required(),
		location: Yup.string().required(),
		summary: Yup.string().required(),
		value: profileTypeId === 3 ? Yup.string() : Yup.string().required(),
		year_completed: Yup.string().required(),
		head_contractor: Yup.string(),
		client_name: Yup.string().required(),
		sector_id: Yup.array().of(Yup.string()).required(),
		trade_value: profileTypeId === 3 ? Yup.string().required() : Yup.string(),
		disciplines:
			profileType === PROFILE_TYPE_CONSULTANT
				? Yup.array().of(
					Yup.object().shape({
						expertise: Yup.string().required("Expertise is required"),
						disciplines: Yup.array().min(
							1,
							"You must select at least one discipline"
						),
					})
				)
				: Yup.array(),
	});

	if (initialValues.name === undefined) {
		/* @ts-ignore */
		return <Skeleton count={5} height="20px" />;
	}

	const makeSelectHandler = (
		fieldName: string,
		mapValue: (selected: SelectOption | SelectOption[] | null) => any
	) => (
		setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
	) => (selected: SelectOption | SelectOption[] | null) => {
		setFieldValue(fieldName, mapValue(selected), true);
	};

	const handleSectorChange = makeSelectHandler("sector_id", (s) =>
		Array.isArray(s) ? s.map((o) => o.value) : []
	);

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			validateOnBlur
			validateOnChange
		>
			{(props) => {
				const {
					setFieldValue,
					isSubmitting,
					values,
					submitForm,
					setStatus,
					status,
					setErrors,
					errors,
					validateForm,
				} = props;

				const onSubmitValidationError = () => {
					setStatus("required");
					setErrorStatus(true);
				};
				const onMaxCharacter = () => {
					setStatus("maxCharacter");
					setErrorStatus(true);
				};

				return (
					<Form
						id="project-form"
						onChange={() => {
							setErrorStatus(false);
							setStatus(null);
						}}
					>
						{!supplierSmartField ? (
							<TabHeader
								heading={editForm ? "Edit Project" : "Create Project"}
							/>
						) : null}
						{supplierSmartField && (
							<SmartFieldNote
								type="project"
								className="-mt-6 mb-8"
							/>
						)}
						<div className="pb-12">
							<div
								id="wrapper__supplier-search-form"
								className="space-y-2"
							>
								<div className="flex flex-col gap-x-6 lg:grid lg:grid-cols-2 lg:gap-y-2">
									<FormikInputWithLabel
										label="Project Name"
										testId="cprofile-project-name"
										required
										name="name"
										type="text"
									/>
									<Location
										label="Project Location"
										required="true"
										name="location"
										setFieldValue={setFieldValue}
										/* @ts-ignore */
										initialValue={initialValues.location}
										rfq
										inputTestId="primary-address-input"
										dropdownTestId="primary-address-dropdown"
										rfqIcon="search-lg"
										projectForm={false}
										allowInternational
									/>
									<div className="col-span-2">
										{/* <FormikRichTextEditor name="summary" label="Project Summary" required testId="cprofile-project-summary" />
										@ts-ignore */}
										<FormattedInput
											label="Project Summary"
											required="true"
											name="summary"
											type="text"
											inputTestId="cprofile-project-summary"
											rfq
											characterLimit={1500}
											setErrors={setErrors}
											onMaxCharacter={onMaxCharacter}
											onChangeHandler={() => {
												setErrorStatus(false);
												setStatus(null);
											}}
											guideLine={
												profileTypeId === 3
													? "Tell us about this project and your company's role"
													: "Tell us about the project, your company's role, scope and/or services provided."
											}
										/>
									</div>
									{profileTypeId === 3 && (
										<Trades
											name="trade_ids"
											testId="cprofile-project-trades"
											guideLine="Identify the trade package/s your company delivered"
										/>
									)}
									{profileTypeId !== 3 && (
										<Currency
											label={
												profileTypeId === 3
													? "Trade Package Value(Total)"
													: "Project Construction Value (AUD) "
											}
											required="true"
											name="value"
											type="number"
											lang="en_EN"
											setFieldValue={setFieldValue}
											values={values}
											guideLine={
												profileTypeId !== 3 &&
												"If construction value is confidential, enter '0'"
											}
										/>
									)}

									{profileTypeId === 3 && (
										<TradeValue
											name="trade_value"
											testId="cprofile-project-trade-value"
											required="true"
											values={values}
											setFieldValue={setFieldValue}
											defaultValue={initialValues.trade_value}
										/>
									)}
									<YearCompleted
										name="year_completed"
										testId="cprofile-project-year-completed"
										required
										defaultValue={values.year_completed}
									/>
									{profileTypeId !== 3 && (
										/* @ts-ignore */
										<ProcurementMethodSelector
											name="procurement_method_ids"
											testId="cprofile-project-procurement-method"
											required="true"
											setFieldValue={setFieldValue}
											values={values}
											guideLine="https://api.procuracon.com.au/api/v1/download/5997d9e56fe777b2ee96b6166eb22afe62883b2cba4a8387cb15c670a79ed6c9"
										/>
									)}
									<FormikInputWithLabel
										label="Client Name"
										testId="cprofile-project-client-name"
										required
										name="client_name"
										type="text"
									/>
									<SectorSelect name="sector_id" label="Sectors" testId="cprofile-project-sector" required isMulti
										// @ts-ignore
										selectedIds={values.sector_id} onChange={handleSectorChange(setFieldValue)} />

									{profileTypeId !== 3 && (
										<FormikInputWithLabel
											label="Type of Funding"
											testId="cprofile-project-funding-type"
											name="funding_type"
											type="select"
											options={fundingTypeOptions}
										/>
									)}
									{profileType === PROFILE_TYPE_CONSULTANT && (
										<div className="col-span-2">
											<ExpertiseRepeater
												testId="project-expertise-discipline"
												name="disciplines"
												/* @ts-ignore */
												values={initialValues.disciplines}
												profileType={profileType}
												required
												discription="Specify your project scope or specialisation in this project to help clients with their Search."
											/>
										</div>
									)}
									{profileTypeId !== 3 && (
										<div className="col-span-2">
											<FormLabel label="Project Area" />
											<div className="flex flex-col gap-x-6 lg:grid lg:grid-cols-2 lg:gap-y-2">
												<div className="flex items-center min-h-[65px]">
													<CheckboxWithLabel
														className="!mb-0 mr-4 shrink-0"
														fontSize="15px"
														label="Site Area (Ha)"
														checked={siteAreaChecked}
														onChange={() => {
															if (!siteAreaChecked === false) {
																setFieldValue("site_area", "");
															}
															setSiteAreaChecked(!siteAreaChecked);
														}}
													/>
													{siteAreaChecked && (
														<FormikInputWithLabel
															testId="cprofile-area-input"
															placeholder="Enter Site Area"
															name="site_area"
															type="text"
															noMargin
															className="max-w-[300px] flex-grow"
														/>
													)}
												</div>
												<div className="flex items-center min-h-[65px]">
													<CheckboxWithLabel
														className="!mb-0 mr-4 shrink-0"
														fontSize="15px"
														label="Gross Floor Area (m2)"
														checked={grossAreaChecked}
														onChange={() => {
															if (!grossAreaChecked === false) {
																setFieldValue("gross_floor_area", "");
															}
															setGrossAreaChecked(!grossAreaChecked);
														}}
													/>
													{grossAreaChecked && (
														<FormikInputWithLabel
															testId="cprofile-gross-input"
															placeholder="Enter Gross Floor Area"
															name="gross_floor_area"
															type="text"
															noMargin
															className="max-w-[300px] flex-grow"
														/>
													)}
												</div>
											</div>
										</div>
									)}
									<div className="col-span-2">
										<FormikMultiImagePreview
											label="Images"
											name="images"
											maxImages={10}
											modalTitleOverride="Image Preview"
										/>
									</div>
									{profileTypeId !== 3 && (
										<>
											<div className="col-span-2">
												{/* @ts-ignore */}
												<FormattedInput
													label="Innovation Summary"
													name="innovation_value_summary"
													type="text"
													className="innovation_value_summary mt-6"
													inputTestId="cprofile-project-innovation-summary"
													characterLimit={1500}
													setErrors={setErrors}
													onMaxCharacter={onMaxCharacter}
													guideLine="Tell us about any innovation / point of difference / value added to the project or how you overcame any challenges during the project."
												/>
											</div>
											<div className="col-span-2">
												<FormikInputWithLabel
													label="Awards"
													testId="cprofile-project-awards"
													name="awards"
													type="textarea"
													maxLength={350}
													showCharacterCount
												/>
											</div>
											<div className="col-span-2">
												{/* @ts-ignore */}
												<FileUpload
													label="Client Testimonial"
													testId="cprofile-upload-testimonials"
													name="testimonials"
													className="col xl-12"
													types={["jpg", "jpeg", "png", "svg", "pdf"]}
													maxFileSize={10}
												/>
											</div>
										</>
									)}

									{status && !isEmpty(errors) && (
										<div className="col-span-2">
											<FlashError
												heading={errorMessages[status]?.heading || status}
												text={errorMessages[status]?.description || status}
												margin="0px 0px 20px 0px"
											/>
										</div>
									)}
								</div>
							</div>

							{additionalFields}
							{supplierSmartField && <SmartFieldNote className={''} type="project" />}

							{customButtons ||
								(!hideDefaultButtons && (
									<FormButtonRow className="!mt-12">
										<div className="flex gap-4">
											<Button
												type="button"
												isLoading={values.status === "published" && isSubmitting}
												loadingText="Saving..."
												disabled={!isEmpty(errors) || isSubmitting}
												disabledStyle={isSubmitting}
												onClick={() => {
													setFieldValue("status", "published", false);
													editForm && setAutoUpdateForm?.(true);

													validateForm().then((validationErrors) => {
														if (Object.keys(validationErrors).length === 0) {
															submitForm();
														} else {
															// Mark every invalid field as touched so red borders appear
															const touchedFields = Object.keys(validationErrors).reduce<Record<string, boolean>>(
																(acc, key) => ({ ...acc, [key]: true }),
																{}
															);
															props.setTouched(touchedFields);
															setErrors(validationErrors);
															setStatus("required");
															setErrorStatus(true);
														}
													});
												}}
												data-testid="cprofile-save-to-profile"
											>
												Save to Profile
											</Button>

											<Button
												type="button"
												btntype="secondary"
												onClick={() => {
													editForm && setAutoUpdateForm?.(true);
													setFieldValue("status", "draft", false);
													submitForm();
												}}
												disabled={isSubmitting}
												disabledStyle={isSubmitting}
												isLoading={values.status === "draft" && isSubmitting}
												loadingText="Saving..."
												data-testid="cprofile-save-draft"
											>
												Save Draft
											</Button>
										</div>

										{handleDelete && (
											<Button
												type="button"
												btntype="plain-error"
												onClick={handleDelete}
												disabled={isRequesting}
												disabledStyle={isRequesting}
												data-testid="delete-project"
											>
												{!isRequesting ? "Delete Project" : "Deleting..."}
											</Button>
										)}
									</FormButtonRow>
								))}
							<OnSubmitValidationError callback={onSubmitValidationError} />
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};
